<template>
  <form
    class="flex flex-col font-ds"
    @submit.prevent="form.handleSubmit()"
  >
    <!--
      The sentence stays on screen, quoted and editable. It is the wizard's
      whole warrant for what it filled in — hiding it would leave the user
      unable to see why a field says what it says.
    -->
    <div
      v-if="sentence"
      class="px-6 pt-5 pb-4 border-b border-border"
    >
      <div class="flex items-center gap-2.5 px-3.5 py-2.5 bg-accent rounded-ds-md">
        <Quote class="size-3.5 shrink-0 text-brand" />
        <p class="flex-1 min-w-0 text-sm font-medium text-accent-foreground">
          {{ sentence }}
        </p>
        <DSButton
          variant="link"
          size="sm"
          class="shrink-0 h-auto px-0 font-bold"
          @click="emit('edit-sentence')"
        >
          <Pencil class="size-3" />
          Edit
        </DSButton>
      </div>
    </div>

    <!--
      The blocker, stated once, with a way to each field. Only on the summary:
      inside a step the outstanding fields are the ones on screen.
    -->
    <MissingFieldsBanner
      v-if="isSummary && missing.length"
      :fields="missing"
      :active-key="lastFocusedField"
      @focus-field="handleFocusField"
    />

    <div class="flex flex-col lg:flex-row lg:items-stretch">
      <WizardSteps
        :steps
        :active-key="stepKey"
        :has-sentence
        @select="stepKey = $event"
      />

      <div class="flex-1 min-w-0 p-6">
        <div class="flex items-center justify-between gap-3 mb-4.5">
          <!--
            A dropdown only where there is something to switch to: the spec's
            cost-of-being-wrong rule is that a misread flow costs one click to
            correct, and the correction that exists is within a family — a
            purchase whose financing was missed. A loan cannot become a
            property, so offering it is a control that either does nothing or
            throws away a half-filled form.
          -->
          <DSSelect
            v-if="switchable.length > 1"
            :model-value="definition.flow"
            @update:model-value="emit('update:flow', $event as AssetWizardFlow)"
          >
            <DSSelectTrigger class="w-auto gap-2.5 shadow-ds-xs">
              <component
                :is="definition.icon"
                class="size-4 shrink-0 text-brand"
              />
              <DSSelectValue class="max-sm:max-w-48" />
            </DSSelectTrigger>
            <DSSelectContent>
              <DSSelectItem
                v-for="option in switchable"
                :key="option"
                :value="option"
              >
                {{ WIZARD_BY_FLOW[option].label }}
              </DSSelectItem>
            </DSSelectContent>
          </DSSelect>

          <!--
            The same line, as a statement. Kept on the icon and the weight the
            trigger uses so the two read as one thing said two ways, rather
            than as two different headings.
          -->
          <p
            v-else
            class="flex items-center gap-2.5 min-w-0 text-lg font-bold text-foreground"
          >
            <component
              :is="definition.icon"
              class="size-4 shrink-0 text-brand"
            />
            <span class="truncate">{{ definition.label }}</span>
          </p>
        </div>

        <!--
          The rail says where you are; the heading says what is wanted. A step
          that has a question of its own asks it here — "Where does this sit?"
          rather than "Ownership & account" a second time.
        -->
        <template v-if="!isSummary">
          <h3 class="flex items-center gap-2 mb-1 text-base font-bold tracking-ds-snug text-foreground">
            {{ step.heading ?? step.label }}
            <!--
              A step that is not what it looks like says so beside its own
              question: "What is it worth?" reads as one more thing to fill in
              until the word Optional is next to it.
            -->
            <DSBadge
              v-if="step.badge"
              tone="neutral"
              variant="soft"
              size="sm"
            >
              {{ step.badge }}
            </DSBadge>
          </h3>
          <p
            v-if="step.description"
            class="mb-4.5 text-sm font-medium leading-normal text-muted-foreground"
          >
            {{ step.description }}
          </p>
        </template>

        <!-- A step is one of five things, and which one is a property of its definition. -->
        <component
          :is="step.component"
          v-if="step.component"
          :form
          :fields="step.fields ?? []"
          :origins
          :owner-account="answer?.ownerAccount"
          :investors="answer?.investors"
          :entities="answer?.entities"
          :cash-flows="answer?.cashFlows"
          :transaction-type
          :flow="definition.flow"
          :sentence
        />

        <WizardTagsStep
          v-else-if="step.key === TAGS_STEP_KEY"
          :selected="tags"
          :suggestions="tagSuggestions"
          :loading="isSuggestingTags"
          :askable="tagsAskable"
          @accept="handleToggleTag"
          @remove="handleToggleTag"
          @add="isTagPickerOpen = true"
          @ask="handleAskTagSuggestion"
        />

        <WizardSummary
          v-else-if="isSummary"
          :form
          :definition
          :origins
          :readonly-fields
          :note="answer?.description"
          :tags
          :tag-suggestions
          :tags-loading="isSuggestingTags"
          :tags-askable="tagsAskable"
          @accept-tag="handleToggleTag"
          @remove-tag="handleToggleTag"
          @add-tag="isTagPickerOpen = true"
          @ask-tags="handleAskTagSuggestion"
          @edit-account="handleEditAccount"
          @edit-investors="handleEditInvestors"
          @edit-entities="handleEditEntities"
          @edit-value="handleEditValue"
          @edit-cash-flows="handleEditCashFlows"
        />

        <WizardStepPlaceholder
          v-else-if="step.placeholder"
          :label="step.label"
          :question="step.placeholder.question"
          :suggestions="step.placeholder.suggestions"
          :answered="answeredPlaceholders.includes(step.key)"
          @answer="handlePlaceholderAnswer"
        />

        <WizardFields
          v-else
          :form
          :fields="step.fields ?? []"
          :origins
        />
      </div>
    </div>

    <div class="flex flex-col gap-3 px-6 py-4 bg-background border-t border-border sm:flex-row sm:items-center sm:justify-between">
      <!--
        On a step, what that step owes — a total is no use to someone filling in
        the one in front of them. On the summary, the total, because that is
        where the outstanding fields are gathered.
      -->
      <div class="flex items-center gap-4 min-w-0">
        <p class="text-xs font-medium text-muted-foreground">
          <!--
            Not while it is running: "nothing is saved" is exactly what stops
            being true the moment the button is pressed.
          -->
          <template v-if="creating">
            Creating the records. Do not close this.
          </template>
          <template v-else-if="isSummary">
            <template v-if="missing.length">
              {{ missing.length }} required {{ missing.length === 1 ? 'field' : 'fields' }} left.
            </template>
            Nothing is saved until you create.
          </template>
          <template v-else-if="stepOutstanding">
            {{ stepOutstanding }} required
            {{ stepOutstanding === 1 ? 'field' : 'fields' }} on this step.
          </template>
          <template v-else>
            Nothing is saved until you create.
          </template>
        </p>
      </div>
      <div class="flex gap-2.5">
        <DSButton
          v-if="stepIndex"
          type="button"
          variant="secondary"
          @click="goToStep(stepIndex - 1)"
        >
          Back
        </DSButton>
        <DSButton
          v-if="!isLastStep"
          type="button"
          data-refid="Next step"
          @click="goToStep(stepIndex + 1)"
        >
          Next
        </DSButton>
        <DSButton
          v-else
          type="submit"
          :disabled="!!missing.length || creating"
          data-refid="Create"
        >
          <LoaderCircle
            v-if="creating"
            class="size-4 animate-spin motion-reduce:animate-none"
          />
          {{ creating ? 'Creating…' : `Create` }}
        </DSButton>
      </div>
    </div>

    <WizardTagPicker
      v-model:open="isTagPickerOpen"
      :selected="tags"
      :suggestions="tagSuggestionsWithReasons"
      :loading="isSuggestingTags"
      :askable="tagsAskable"
      @ask="handleAskTagSuggestion"
      @toggle="handleToggleTag"
    />
  </form>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { LoaderCircle, Pencil, Quote } from '@lucide/vue'

import {
  AssetWizardAnswer,
  AssetWizardFlow,
} from '@/store/ai/assetWizard/utils/types'
import { TagRef, TagSuggestionWithOrigin } from '@/entities/tags/utils/types'

import { useAITagSuggestionStore } from '@/store/ai/tagSuggestion'
import { useAssetsStore } from '@/store/assets'
import { useSettingsStore } from '@/store/settings'
import { useTagsStore } from '@/store/tags'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { handleCatchedError } from '@/helpers/common'
import { isCommonTag, isSameTag, withTagId, withTagIds } from '@/entities/tags/utils/helpers'

import {
  DSBadge,
  DSButton,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
} from '@ds'

import MissingFieldsBanner from './components/MissingFieldsBanner.vue'
import WizardFields from './components/WizardFields.vue'
import WizardStepPlaceholder from './components/WizardStepPlaceholder.vue'
import WizardSteps from './components/WizardSteps.vue'
import WizardSummary from './components/WizardSummary.vue'
import WizardTagPicker from './components/WizardTagPicker.vue'
import WizardTagsStep from './components/WizardTagsStep.vue'

import { ENTITIES_STEP_KEY, INVESTORS_STEP_KEY, SUMMARY_STEP_KEY, TAGS_STEP_KEY } from './utils/const'
import {
  aiFilledFields,
  allFields,
  missingFields,
  originsFor,
  outstandingFields,
  cashFlowsStepOf,
  entitiesStepOf,
  investorsStepOf,
  ownershipStepOf,
  valueStepOf,
  resolveDefaults,
  seedValues,
  wizardSteps,
} from './utils/helpers'
import { useWizardOptions } from './utils/options'
import { switchableFlows } from './utils/flows'
import { provideWizardFocus } from './utils/focus'
import { WIZARD_BY_FLOW } from './utils/registry'
import { WizardDefinition, WizardValues } from './utils/types'

/*
 * Every wizard is this component, driven by the definition in its own folder.
 *
 * One TanStack form per wizard, validated against that folder's zod schema, so
 * the steps are views onto one state object rather than five little forms that
 * have to be reconciled. The rail is a map, not a gate: every step is reachable
 * at any time, and what stops a create is an unfilled required field, not
 * where the user happens to be standing.
 *
 * The form is created at setup from the definition, so switching flow remounts
 * this component (the dialog keys it on the flow) rather than mutating a form
 * that was built for a different schema.
 */
const { definition, answer, sentence, creating } = defineProps<{
  definition: WizardDefinition
  /** Present when the assistant opened this. Its fields arrive prefilled and read-only. */
  answer?: AssetWizardAnswer
  /** What the user typed, when they typed anything. */
  sentence?: string
  /**
   * The create is running. Owned by the dialog, which is what performs it —
   * this is only how the wizard knows to stop offering the button again.
   */
  creating?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:flow', flow: AssetWizardFlow): void
  (e: 'edit-sentence'): void
  (e: 'create', values: WizardValues): void
}>()

const transactionsSettingsStore = useTransactionsSettingsStore()
const tagSuggestionStore = useAITagSuggestionStore()
const assetsStore = useAssetsStore()
const tagsStore = useTagsStore()
const settingsStore = useSettingsStore()

const isAIEnabled = computed(() => settingsStore.getAIAgent)

const { accountIds, accountsOf, accountTypesFor, defaultFor } = useWizardOptions()

/*
 * The transaction the first record will post, which is what decides the account
 * types this wizard may hang anything off: 'add liability' for a loan, 'add
 * position' for a property. Read from the record rather than declared twice —
 * the summary already states it on every card.
 */
const transactionType = computed(() => definition.records[0]?.transactionType ?? '')

/*
 * Which accounts exist here, for checking what the read came back with.
 *
 * The read is shown every account in the repository and answers with an id; the
 * ownership step only offers the ones this flow's transaction accepts. So an id
 * is believed only when the step could have offered it — see WizardSeedContext.
 */
const seedContext = computed(() => ({
  ownerAccounts: accountsOf(transactionType.value),
  ownerAccountTypes: accountTypesFor(transactionType.value),
  accountIds: accountIds(),
}))

/*
 * The repository's tags, once they are actually known.
 *
 * `initFlag` rather than a length check: an empty list is a real answer for a
 * repository with no tags yet, while an unloaded one is not an answer at all —
 * matching against it would strip the id off every applied tag and have the
 * create POST pairs that already exist. Undefined until it has loaded, which is
 * the two resolvers' cue to leave the tags as they are.
 */
const catalogue = computed(() => (tagsStore.initFlag ? tagsStore.getList : undefined))

/**
 * The values with every applied tag's id re-resolved against the catalogue.
 *
 * A backstop, in the same spirit as `resolveDefaults` beside it: tags arrive
 * with their id already resolved, but the ones the assistant read are seeded at
 * setup — before `onMounted` has so much as asked for the catalogue — so
 * without this every tag read from the sentence would reach the create claiming
 * to be new.
 */
const withResolvedTags = (values: WizardValues): WizardValues =>
  catalogue.value ? { ...values, tags: withTagIds(values.tags ?? [], catalogue.value) } : values

/*
 * Fields register themselves here as they mount, so the banner can reach one by
 * name — see utils/focus. Provided rather than passed because the components in
 * between have nothing to do with focus.
 */
const wizardFocus = provideWizardFocus()

const form = useForm({
  /*
   * The repository's defaults are in the form from the start, so a defaulted
   * field shows the value it will be created with rather than a promise of one.
   * Applied over the seed, and only where it is empty, so a currency the
   * assistant read from the sentence still wins.
   */
  defaultValues: resolveDefaults(definition, seedValues(definition, answer, seedContext.value), defaultFor),
  validators: { onChange: definition.schema },
  /*
   * Applied again, as a backstop. The seed above normally fills these, but the
   * repository is read at setup and a store that had not loaded yet would have
   * had no currency to offer — in which case the placeholder is still on
   * screen making the promise, and this is what keeps it.
   */
  onSubmit: ({ value }) =>
    emit('create', withResolvedTags(resolveDefaults(definition, value, defaultFor))),
})

const values = form.useSelector(state => state.values)

/*
 * The assistant's answer opens the wizard where the work is left — the summary —
 * because it has already been through the earlier steps on the user's behalf.
 * Without it there is nothing to review, so the wizard starts at step one.
 */
const stepKey = ref(answer ? SUMMARY_STEP_KEY : definition.steps[0].key)

const isTagPickerOpen = ref(false)

/** Placeholder steps that have been answered, so the disclaimer can be honest. */
const answeredPlaceholders = ref<string[]>([])

const stepIndex = computed(() =>
  Math.max(definition.steps.findIndex(item => item.key === stepKey.value), 0),
)

const step = computed(() => definition.steps[stepIndex.value])

const isLastStep = computed(() => stepIndex.value === definition.steps.length - 1)

const isSummary = computed(() => step.value.key === SUMMARY_STEP_KEY)

/*
 * The steps the user has actually opened, so an optional one can tell "left
 * empty" from "never been there" — see wizardSteps. Written by the watcher
 * below rather than by `goToStep`: the rail, the banner's chips and the
 * summary's account link all move the wizard too, and a list of visits that
 * only some of them updated would be worse than none.
 */
const visited = ref<string[]>([])

watch(
  stepKey,
  key => {
    if (!visited.value.includes(key)) visited.value = [...visited.value, key]
  },
  { immediate: true },
)

const steps = computed(() => wizardSteps(definition, values.value, visited.value))

/** What the step on screen still owes, which is what its footer reports. */
const stepOutstanding = computed(() => outstandingFields(step.value, values.value).length)

const missing = computed(() => missingFields(definition, values.value))

const filled = computed(() => aiFilledFields(definition, answer, seedContext.value))

const origins = computed(() => originsFor(definition, filled.value, values.value))

/*
 * Whether the assistant has anything to go on. Without a sentence every
 * suggestion would be drawn from the repository's own lists alone — which is
 * not a reading of anything, just the wizard guessing.
 */
const hasSentence = computed(() => Boolean(sentence?.trim()))

/*
 * What shows as text on the summary rather than as a control.
 *
 * With a sentence, what the assistant filled: the rest is still the user's to
 * type, and the summary is where they type it. What the assistant read stays
 * editable in the step that owns it, so a misread value is never a dead end.
 *
 * Without one, everything. Nothing is marked as read, so the summary would
 * otherwise be the same column of controls the steps just showed — a second
 * pass at the form rather than a review of it. Empty fields included: the
 * banner above already names every one that is owed and walks the user to its
 * step, which is a better way back than an input sitting on a card.
 */
const readonlyFields = computed(() => {
  if (!isSummary.value) return []
  if (!hasSentence.value) return allFields(definition).map(field => field.name)
  return [...filled.value]
})

/*
 * The chip the user last followed, marked in the banner when they come back to
 * the summary — a field still listed after a visit is the one to look at
 * again, and saying which was already visited is the difference between a list
 * and a worked list.
 */
const lastFocusedField = ref<string>()

/** What this flow may be corrected to, itself included — see switchableFlows. */
const switchable = computed(() => switchableFlows(definition.flow))

const tags = computed<TagRef[]>(() => values.value.tags ?? [])

/*
 * The tag assistant's own suggestions — the only source now that the initial
 * read never guesses tags at all (see AssetWizardAnswer). Deduped by pair,
 * with the applied ones removed: a suggestion is not a selection, and one
 * already accepted is not a suggestion.
 */
const tagSuggestionsWithReasons = computed<TagSuggestionWithOrigin[]>(() => {
  const merged: TagSuggestionWithOrigin[] = []

  for (const tag of tagSuggestionStore.getSuggestions) {
    /*
     * Never Category or Subcategory. The prompt is never shown them, so this
     * is the backstop for a model that names one anyway.
     */
    if (!isCommonTag(tag)) continue
    if (tags.value.some(applied => isSameTag(applied, tag))) continue
    if (merged.some(item => isSameTag(item, tag))) continue
    merged.push(tag)
  }

  return merged
})

/** The chips row wants the pairs alone. */
const tagSuggestions = computed<TagRef[]>(() => tagSuggestionsWithReasons.value)

/** The wait, wherever a question was actually asked. */
const isSuggestingTags = computed(() => tagSuggestionStore.isLoading)

/*
 * Offers the "Ask AI" chip until the assistant has actually been asked —
 * whatever it comes back with. Askable with or without a sentence, same as the
 * suggest call itself: see useAITagSuggestionStore.
 */
const tagsAskable = computed(() => isAIEnabled.value && !tagSuggestionStore.hasAsked)

const setTags = (next: TagRef[]) => {
  form.setFieldValue('tags', next)
}

const goToStep = (index: number) => {
  const target = definition.steps[index]
  if (!target) return
  stepKey.value = target.key
}

/*
 * A chip in the banner opens the step that collects the field *and* puts the
 * cursor in it. Switching step alone left the user to find one field among
 * five, which is most of the work the chip was meant to save.
 *
 * The focus waits for the step to render — the field does not exist in the DOM
 * until then, and on the summary it may exist twice. `nextTick` rather than a
 * timeout because a render is exactly what is being waited for.
 */
const handleFocusField = async (name: string) => {
  /*
   * The one chip missingFields raises for a step rather than a field — an
   * investor row is never a named one, so there is nothing here to focus,
   * only the step to open. See handleEditInvestors.
   */
  if (name === INVESTORS_STEP_KEY) {
    handleEditInvestors()
    return
  }

  /** Same reasoning as INVESTORS_STEP_KEY above, for private_fund's own entities step. */
  if (name === ENTITIES_STEP_KEY) {
    handleEditEntities()
    return
  }

  const owner = definition.steps.find(item => item.fields?.some(field => field.name === name))
  if (!owner) return

  stepKey.value = owner.key
  lastFocusedField.value = name
  await nextTick()

  /*
   * A field that cannot be focused is not an error worth surfacing: a step
   * still being built draws no controls, and the step switch is already the
   * useful half of the answer.
   */
  wizardFocus.focus(name)
}

/*
 * The summary's account block, back to the step that sets it.
 *
 * Lands on the field that is actually owed where one is — the block's amber
 * mark and the banner's chips are the same promise, and opening the step with
 * the cursor beside the picker is what that promise is worth. With nothing
 * outstanding it is a plain visit: the account is set, and the user is going
 * there to change it rather than to supply it.
 */
const handleEditAccount = () => {
  const owner = ownershipStepOf(definition)
  if (!owner) return

  const [outstanding] = outstandingFields(owner, values.value)
  if (outstanding) {
    handleFocusField(outstanding.name)
    return
  }

  stepKey.value = owner.key
}

/** The summary's value block, back to the step that records them. */
const handleEditValue = () => {
  const owner = valueStepOf(definition)
  if (owner) stepKey.value = owner.key
}

/** The summary's investing-entities block, back to the step that splits it. */
const handleEditInvestors = () => {
  const owner = investorsStepOf(definition)
  if (owner) stepKey.value = owner.key
}

/** private_fund's own entities block, back to the step that lists them. */
const handleEditEntities = () => {
  const owner = entitiesStepOf(definition)
  if (owner) stepKey.value = owner.key
}

/** The summary's cash-flows block, back to the step that logs them. */
const handleEditCashFlows = () => {
  const owner = cashFlowsStepOf(definition)
  if (owner) stepKey.value = owner.key
}

/*
 * One tap accepts, exactly as the spec describes — the chip carries the tag, so
 * accepting one is the same operation as toggling it in the picker.
 *
 * The applied pair keeps the repository's id wherever the repository has that
 * pair, which is what separates linking an existing tag from creating one.
 * Resolved here rather than read off the tag: a chip, a suggestion and a picker
 * row all arrive down this one path, and only the picker's rows carry an id at
 * all.
 */
const handleToggleTag = (tag: TagRef) => {
  /*
   * Refused rather than filtered further up alone: the picker's create row
   * takes a dimension the user types, and a typed "Category" would name the
   * reserved one however carefully the offered list was narrowed.
   */
  if (!isCommonTag(tag)) return

  if (tags.value.some(item => isSameTag(item, tag))) {
    setTags(tags.value.filter(item => !isSameTag(item, tag)))
    return
  }

  const pair: TagRef = { tag_name: tag.tag_name, tag_value: tag.tag_value }

  setTags([...tags.value, catalogue.value ? withTagId(pair, catalogue.value) : pair])
}

/*
 * MOCKED. A placeholder step records nothing — the answer is acknowledged and
 * dropped, which the step says out loud rather than implying it was saved.
 */
const handlePlaceholderAnswer = () => {
  if (answeredPlaceholders.value.includes(step.value.key)) return
  answeredPlaceholders.value = [...answeredPlaceholders.value, step.value.key]
}

/*
 * Asked only once the user clicks "Ask AI" on the chips, wherever they are on
 * screen — the tags step or the summary. The answer is worth having with or
 * without a sentence, but it is drawn from the flow and the repository's
 * vocabulary rather than from anything the user typed, so nothing is lost by
 * waiting for the click. See the store on how the absence is put to the model.
 */
const handleAskTagSuggestion = () => {
  tagSuggestionStore.suggest({
    sentence,
    flow: definition.flow,
    existing: tagsStore.getList,
    applied: tags.value,
  })
}

onMounted(() => {
  // The account-type selects read these. The wizard can be reached without ever
  // touching a transactions view, so nothing else guarantees they are loaded.
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch().catch(e => handleCatchedError(e as string))
  }

  /*
   * The tag picker searches these, and the tag assistant is told which pairs
   * already exist from the same list — so an unloaded catalogue would not just
   * empty the picker, it would have every suggestion come back marked as a new
   * pair. Reached from the app shell, so nothing else has loaded them either.
   */
  if (!tagsStore.initFlag) {
    tagsStore.fetch().catch(e => handleCatchedError(e as string))
  }

  /*
   * The repository's assets, which this wizard needs in three places: the
   * ownership step lists the accounts a record may hang off, the create resolves
   * the currency asset the value leg is denominated in, and both read them out
   * of the bunch store. Nothing in the app shell fetches them — the Data views
   * do — so a wizard opened from the sidebar had an empty account picker and a
   * transaction with no currency on it.
   */
  if (!assetsStore.initFlag) {
    assetsStore.fetch().catch(e => handleCatchedError(e as string))
  }
})
</script>
